import { computed, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { CurrentUrlService } from '@cumlaude/shared-services';
import { AttrPath } from '@cumlaude/metadata';
import { AttOverrides } from '@cumlaude/shared-dashboard-registry';
import { CumLaudeRoute } from '@cumlaude/shared-components-menu';
import { QueryParamStateService } from './query-param-state.service';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserRouteData } from './user-routes.service';
import { EquivalentAttService } from './equivalent-att.service';

const GEEN_OVERRIDES: AttOverrides = {};

@Injectable({
	providedIn: 'root',
})
export class GroeperingService {
	readonly router = inject(Router);
	readonly currentUrlService = inject(CurrentUrlService);
	readonly qp: QueryParamStateService = inject(QueryParamStateService);
	private readonly equivalentAttService = inject(EquivalentAttService);

	/**
	 * De route-data van alle dashboards, op volledige url.
	 */
	private readonly routeDataPerUrl = computed(() => this.getRouteDataPerUrl(this.router.config));

	private readonly huidigeRouteData = computed(() => {
		const currentPath = this.currentUrlService.currentPath();
		return currentPath ? this.routeDataPerUrl().get(currentPath) : undefined;
	});

	/**
	 * De standaard groeperingen voor het huidige dashboard.
	 */
	defaultGroeperingen = computed(() => this.huidigeRouteData()?.defaultGroeperingen ?? <AttrPath[]>[]);

	/**
	 * De groeperingen die momenteel in de query parameters staan.
	 */
	groeperingenQueryParam = toSignal(this.qp.observe_g());

	/**
	 * De actieve groeperingen, meestal de groeperingen die in de query parameters staan.
	 * Indien deze niet aanwezig zijn, de standaard groeperingen voor het huidige dashboard.
	 */
	huidigeGroeperingen = computed(() => {
		const groeperingenUitQP = this.groeperingenQueryParam();
		if (!groeperingenUitQP) return this.defaultGroeperingen();

		return this.equivalentAttService.naarBeschikbareGroeperingen(groeperingenUitQP, this.huidigeRouteData()?.attOverrides ?? GEEN_OVERRIDES);
	});

	/**
	 * Het aantal actieve groeperingen.
	 */
	huidigeAantalGroeperingen = computed(() => this.huidigeGroeperingen().length);

	/**
	 * Bouwt de route-data per url op door de routeboom af te lopen. Routes zonder data worden
	 * overgeslagen.
	 */
	private getRouteDataPerUrl(
		routes: CumLaudeRoute<UserRouteData>[],
		currentPath?: string[],
		acc: Map<string, UserRouteData> = new Map()
	): Map<string, UserRouteData> {
		for (const route of routes) {
			const path = currentPath ? [...currentPath, route.path!] : [route.path!];

			if (route.children) this.getRouteDataPerUrl(route.children, path, acc);
			if (route.data) acc.set(path.join('/'), route.data);
		}
		return acc;
	}
}
